import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import { getProfitVault } from '../../services/ProfitVaultService';
import '../ProfitMonitor/ProfitMonitor.css';
import './ProfitVault.css';

function formatUsd(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '--';
    return number.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

function formatSignedUsd(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '--';
    const prefix = number > 0 ? '+' : '';
    return `${prefix}${formatUsd(number)}`;
}

function pairLabel(symbol) {
    return String(symbol || '').replace(/USDC$/, '/USDC').replace(/USDT$/, '/USDC');
}

function formatWhen(value) {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
}

function netTone(value) {
    const number = Number(value);
    if (!(number > 0) && !(number < 0)) return 'is-flat';
    return number < 0 ? 'is-loss' : 'is-gain';
}

function apiError(err) {
    return err?.response?.data || err?.message || 'Não foi possível carregar o PnL.';
}

function tradeValues(entry, quote) {
    const buy = Number(entry.buyNet);
    const sell = Number(entry.sellNet);
    const parts = [];
    if (Number.isFinite(buy) && buy > 0)
        parts.push(`compra ${formatUsd(buy)} ${quote}`);
    if (Number.isFinite(sell) && sell > 0)
        parts.push(`venda ${formatUsd(sell)} ${quote}`);
    return parts.join(' · ');
}

function ProfitVault() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState({ type: '', text: '' });

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const result = await getProfitVault();
                if (!cancelled) setData(result);
            } catch (err) {
                if (!cancelled)
                    setNotification({ type: 'error', text: apiError(err) });
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, []);

    const entries = data?.entries || [];
    const quote = data?.quote || 'USDC';
    const net = Number.isFinite(Number(data?.net))
        ? Number(data.net)
        : Number(data?.gained || 0) - Number(data?.lost || 0);
    const todayNet = Number(data?.today ?? 0);
    const pnl = Number.isFinite(Number(data?.pnl)) ? Number(data.pnl) : net;
    const pnlPerc = Number.isFinite(Number(data?.pnlPerc)) ? Number(data.pnlPerc) : null;
    const deposited = Number(data?.depositedUsdc);
    const walletTotal = Number(data?.walletTotal);

    return (
        <>
            <Menu />
            <main className="content">
                <div className="profit-monitor profit-vault">
                    <header className="profit-monitor-header">
                        <h1>PnL</h1>
                        <p>
                            O PnL é o mesmo da carteira: total agora menos o capital depositado.
                            Abaixo ficam as vendas fechadas. Depósitos do administrador não entram no PnL.
                        </p>
                    </header>

                    {loading && !data && (
                        <div className="profit-card profit-loading">Carregando o PnL...</div>
                    )}

                    {data && (
                        <section className="profit-card">
                            <p className="profit-vault-kicker">PnL</p>
                            <div className="profit-vault-total">
                                <p className={`profit-vault-amount ${netTone(pnl)}`}>
                                    {formatSignedUsd(pnl)}
                                    <span>
                                        {quote}
                                        {pnlPerc != null ? ` (${pnlPerc > 0 ? '+' : ''}${pnlPerc.toFixed(2)}%)` : ''}
                                    </span>
                                </p>
                                {Number.isFinite(deposited) && deposited > 0 && (
                                    <p className="profit-vault-caption">
                                        Versus {formatUsd(deposited)} {quote} depositado
                                        {Number.isFinite(walletTotal) && walletTotal > 0
                                            ? ` · carteira ${formatUsd(walletTotal)} ${quote}`
                                            : ''}
                                    </p>
                                )}
                            </div>
                            <div className="profit-vault-metrics">
                                <div>
                                    <span>Desde o início</span>
                                    <strong className={netTone(net)}>{formatSignedUsd(net)} {quote}</strong>
                                </div>
                                <div>
                                    <span>Só hoje</span>
                                    <strong className={netTone(todayNet)}>{formatSignedUsd(todayNet)} {quote}</strong>
                                </div>
                            </div>
                            <div className="profit-vault-metrics">
                                <div>
                                    <span>Vendas no lucro</span>
                                    <strong>{data.count || 0}</strong>
                                </div>
                                <div>
                                    <span>Vendas no stop</span>
                                    <strong>{data.lossCount || 0}</strong>
                                </div>
                            </div>
                        </section>
                    )}

                    <section className="profit-card">
                        <h2>Trades fechados</h2>
                        {loading && !entries.length && (
                            <div className="profit-loading">Buscando vendas...</div>
                        )}
                        {!loading && !entries.length && (
                            <div className="profit-empty">
                                Ainda não há venda fechada. Lucros e stops do Monitor aparecem aqui.
                            </div>
                        )}
                        {entries.length > 0 && (
                            <div className="profit-vault-list">
                                {entries.map(entry => {
                                    const deposit = entry.isDeposit || String(entry.symbol || '').toUpperCase() === 'DEPOSIT';
                                    const loss = !deposit && (Number(entry.profit) < 0 || entry.isLoss);
                                    const values = deposit ? null : tradeValues(entry, quote);
                                    return (
                                        <div className={`profit-vault-row${deposit ? ' is-deposit' : loss ? ' is-loss' : ''}`} key={entry.id}>
                                            <div>
                                                <strong>{deposit ? 'Depósito' : pairLabel(entry.symbol)}</strong>
                                                <span>{deposit ? 'Crédito na carteira' : (loss ? 'Stop' : 'Lucro')} · {formatWhen(entry.createdAt)}</span>
                                                {deposit
                                                    ? <span className="profit-vault-trade">Não entra no PnL</span>
                                                    : (values
                                                        ? <span className="profit-vault-trade">{values}</span>
                                                        : null)}
                                            </div>
                                            <em>{formatSignedUsd(entry.profit)} {quote}</em>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    <div style={{ marginTop: 8 }}>
                        <button
                            type="button"
                            className="profit-btn-secondary"
                            onClick={() => navigate('/profit-monitor')}
                        >
                            Abrir Monitor de Lucros
                        </button>
                    </div>
                </div>
            </main>
            <Footer />
            {notification.text && (
                <Toast
                    type={notification.type}
                    text={notification.text}
                    onClose={() => setNotification({ type: '', text: '' })}
                />
            )}
        </>
    );
}

export default ProfitVault;
